import { diffDays, type ProjectionActualMonth } from '@dairy/shared';
import type { ProjectionMonth } from './projection';
import type { DomainExpense, DomainMilkRecord } from './types';

/** Link eligibility uses the server registration date, just like projection sync validation. */
export function projectionLinkEligible(startDate: string, animal: { createdAt?: unknown; deletedAt?: unknown }) {
  return !animal.deletedAt && typeof animal.createdAt === 'string' && animal.createdAt.slice(0, 10) >= startDate;
}

/** IP-8: recorded production (including withheld milk), dated linked cash expenses, completed months only. */
export function projectionActual(
  months: readonly ProjectionMonth[], animalId: string, asOf: string,
  milk: readonly DomainMilkRecord[], expenses: readonly DomainExpense[],
): ProjectionActualMonth[] {
  const difference = (actual: number, projected: number) => projected === 0 ? null : (actual - projected) / projected;
  return months.filter((m) => m.to <= asOf).map((m) => {
    const records = milk.filter((r) => !r.deletedAt && r.animalId === animalId && r.date >= m.from && r.date < m.to);
    const actualMl = records.reduce((sum, r) => sum + r.ml, 0);
    const actualCostSantim = expenses.filter((e) => !e.deletedAt && e.animalId === animalId && e.date >= m.from && e.date < m.to)
      .reduce((sum, e) => sum + e.amountSantim, 0);
    const projectedCostSantim = m.feedSantim + m.calfMilkSantim + m.youngStockFeedSantim + m.vetSantim + m.laborSantim + m.breedingSantim;
    return {
      month: m.month, from: m.from, to: m.to, projectedMl: m.producedMl, actualMl,
      recordedDays: new Set(records.map((r) => r.date)).size, days: diffDays(m.to, m.from),
      projectedCostSantim, actualCostSantim,
      milkDifference: difference(actualMl, m.producedMl), costDifference: difference(actualCostSantim, projectedCostSantim),
    };
  });
}
